import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import EditableInnerBox from '@repo/ui/editable-box'
import {
  CheckMarkIcon,
  EditFormIcon,
  ExclamationMarkIcon,
  FamilyIcon,
  LocationOneIcon,
  SendIcon,
  TelephoneIcon,
  UserSixIcon,
  UserTwoIcon,
  XIcon
} from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Switch from '@repo/ui/switch'
import TextInformation from '@repo/ui/text-information'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isBefore, startOfDay } from 'date-fns'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { getAAKeywords } from '../../../api/aa-keywords/get-aa-keywords'
import {
  getCountriesList,
  getCountriesListQueryKey
} from '../../../api/countries/get-countries-list'
import { getNationalitiesList } from '../../../api/countries/get-nationalities-list'
import { acceptCanGoOutAloneAuthorization } from '../../../api/enrollments/accept-go-out-alone-authrization'
import {
  authorizeChildToGoOutAlone,
  resendAuthorizationToGoOutAlone,
  unauthorizeChildToGoOutAlone
} from '../../../api/enrollments/can-child-to-go-out-alone'
import { getEnrollmentCanGoOutAlone } from '../../../api/enrollments/get-can-enrollment-go-out-alone'
import { getEnrollment } from '../../../api/enrollments/get-enrollment'
import { rejectCanGoOutAloneAuthorization } from '../../../api/enrollments/reject-go-out-alone-authrization'
import { updateEnrollmentClientContext } from '../../../api/enrollments/update-enrollment'
import {
  newPropositionState,
  updateEnrollmentProposition
} from '../../../api/enrollments/update-enrollment-proposition'
import { updateOtoIndividualProposition } from '../../../api/enrollments/update-oto-individual-proposition'
import { useObserver } from '../../../lib/contexts/observer'
import useAuth from '../../../lib/hooks/use-auth'
import useEnrollment from '../../../lib/hooks/use-enrollment'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  genderOneLetterMapping,
  genderOptionsMapping
} from '../../../lib/types/enums/common/gender.enum'
import { schoolLevelEnumMapping } from '../../../lib/types/enums/common/school-level.enum'
import { contactEnumMapping } from '../../../lib/types/enums/enrollment/contact.enum'
import { OtoIndividualTransferMethodEnum } from '../../../lib/types/enums/enrollment/oto-individual-transfer-method.enum'
import { enrollmentScoreEnumMapping } from '../../../lib/types/enums/enrollment/score.enum'
import { friendPlaceEnumMapping } from '../../../lib/types/enums/friend/friend-place.enum'
import { InsuranceTypeEnum } from '../../../lib/types/enums/insurance/insurance-type.enum'
import { languageLevelEnumMapping } from '../../../lib/types/enums/languages/language-level.enum'
import { familyArrangementEnumMapping } from '../../../lib/types/enums/ol/family-arrangement.enum'
import {
  enrollmentMainMedicalInformationsLockFields,
  getEnrollmentClientContextJourneyFields
} from '../../../lib/types/fields/enrollments/enrollment'
import { CenterEventProperties } from '../../../lib/types/models/center-event/properties.center-event'
import { getCountryLabel } from '../../../lib/types/models/country/country-list'
import {
  EnrollmentProperties,
  ParentInformationType
} from '../../../lib/types/models/enrollment/enrollment.properties'
import { EnrollmentInput } from '../../../lib/types/models/enrollment/enrollment-input'
import { EnrollmentPropositionProperties } from '../../../lib/types/models/enrollment-proposition/enrollment-proposition.properties'
import { EnrollmentPropositionInput } from '../../../lib/types/models/enrollment-proposition/enrollment-proposition-input'
import {
  hasOtoIndividualCatalogue,
  mapOtoIndividualPropositionInputToRequest,
  mapOtoIndividualPropositionToInput,
  OtoIndividualPropositionInput
} from '../../../lib/types/models/enrollment-proposition/oto-individual-proposition-input'
import { InsuranceLight } from '../../../lib/types/models/insurance/insurance'
import { InsuranceProperties } from '../../../lib/types/models/insurance/properties.insurance'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { UpdateInput } from '../../../lib/types/utils'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'
import EnrollmentModificationNotificationModal from '../enrollments/enrollment-modification-notification-modal'
import EnrollmentPropositionInsuranceModal from '../enrollments/enrollment-proposition-insurance-modal'
import EnrollmentPropositionFormModal from '../enrollments/enrolment-create-proposition-steps/enrollment-proposition-form-modal'
import OtoIndividualPropositionFormModal from '../enrollments/enrolment-create-proposition-steps/oto-individual-proposition-form-modal'
import {
  mapEnrollmentPropositionInputToRequest,
  mapEnrollmentPropositionToInput,
  mapEnrollmentToAddressInformationInput,
  mapEnrollmentToChildInformationInput,
  mapEnrollmentToContactInformationInput,
  mapEnrollmentToFriendsInput,
  mapEnrollmentToParentInformationInput
} from '../enrollments/utils/enrollments-utils'
import LabelWithText from '../ols/label-with-text'
import { getDurationInDaysString } from '../products/utils'
import AddressInformationForm from './shared/address-information-form'
import ChildInformationForm from './shared/child-information-form'
import ContactInformationForm from './shared/contact-information-form'
import { EnrollmentMedicalInformation } from './shared/enrollment-medical-information'
import FriendsForm from './shared/friends-form'
import MedicalInformationForm from './shared/medical-information-form'
import ParentInformationForm from './shared/parent-information-form'

type ParentInformationProps = {
  parent: ParentInformationType
  mode?: 'parent' | 'admin'
}

export const ParentInformation: React.FC<ParentInformationProps> = ({
  parent,
  mode = 'admin'
}) => {
  const { data } = useQuery({
    queryKey: ['aa-keywords'],
    queryFn: () => getAAKeywords(),
    enabled: mode === 'admin' // Only fetch keywords in admin mode
  })

  const keywords = data?.data.map((keyword) => keyword.label) ?? []

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-6 mobile:grid-cols-1 mobile:gap-2">
        <TextInformation
          title="Civilité"
          value={valueRender(
            parent.parentGender,
            (parentGender) => genderOptionsMapping[parentGender].title
          )}
        />
        <TextInformation
          title="NOM"
          value={valueRender(parent.parentLastName, (lastName) => lastName.toUpperCase())}
        />
        <TextInformation
          title="Prénom"
          value={valueRender(parent.parentFirstName, (firstName) => firstName)}
        />
      </div>

      <div className="grid grid-cols-3 gap-6 mobile:grid-cols-1 mobile:gap-2">
        <TextInformation
          title="N° de portable"
          value={valueRender(parent.parentPhoneNumber, (phoneNumber) =>
            formatPhoneNumber(phoneNumber)
          )}
        />
        <TextInformation
          className="break-all"
          title="Email"
          value={valueRender(parent.parentEmail, (parentEmail) => parentEmail)}
        />

        <LabelWithText
          label="Profession"
          dataTextColor={CenterEventProperties.professionColorBasedOnKeywords(
            keywords,
            parent.parentProfession
          )}
          data={valueRender(parent.parentProfession)}
        />
      </div>
      <div className="grid grid-cols-1 gap-6">
        <TextInformation
          title="Tél travail"
          value={valueRender(parent.parentPhoneNumber2, (phoneNumber2) =>
            formatPhoneNumber(phoneNumber2)
          )}
        />
      </div>
    </div>
  )
}

type EnrollmentProfileGlobalViewProps = {
  mode?: 'parent' | 'admin'
  showEmptyMedicalInformation?: boolean
}

const EnrollmentProfileGlobalView = ({
  mode = 'admin',
  showEmptyMedicalInformation = false
}: EnrollmentProfileGlobalViewProps) => {
  const { enrollment, setEnrollment } = useEnrollment()
  const { user } = useAuth()
  const { t } = useTranslate()
  const observer = useObserver({ key: 'enrollment-global-view' })
  const { currentLanguage } = useTranslate()

  const { selectedData, modalState, toggleModal, closeModel, closeAll } = useModalManager<
    | 'general-information'
    | 'parent-information'
    | 'address-information'
    | 'contact-information'
    | 'authorization-child'
    | 'declared-friends'
    | 'medical-information'
    | 'select-journey'
    | 'select-insurance'
    | 'edit-oto'
    | 'oto-insurance',
    number
  >([
    'general-information',
    'parent-information',
    'address-information',
    'contact-information',
    'authorization-child',
    'declared-friends',
    'medical-information',
    'select-journey',
    'select-insurance',
    'edit-oto',
    'oto-insurance'
  ])

  const {
    modalState: notificationModalState,
    toggleModal: toggleNotificationModal,
    closeModel: closeNotificationModal
  } = useModalManager<'email-notification' | 'no-space-to-assign'>([
    'email-notification',
    'no-space-to-assign'
  ])

  const { data: countries } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList()
  })

  const { data: nationalities } = useQuery({
    queryKey: ['nationalities'],
    queryFn: getNationalitiesList
  })

  const { data: canGoOutAlone } = useQuery({
    queryKey: ['can-enrollment-go-out-alone', enrollment?.id],
    queryFn: () => getEnrollmentCanGoOutAlone(enrollment.id),
    enabled: !!enrollment?.id
  })

  // only LEC edits the health information here, and the backend decides whether the main field is open
  const { data: mainMedicalInformationsLock } = useQuery({
    queryKey: ['enrollment', enrollment?.id, enrollmentMainMedicalInformationsLockFields],
    queryFn: () => getEnrollment(enrollment.id, enrollmentMainMedicalInformationsLockFields),
    enabled: !!enrollment?.id && mode === 'admin' && user.isMasterLec
  })

  const isEnrollmentConfirmed = !!enrollment.confirmedAt
  // the profile selection can't filter relations: a place held from the center has no student to show
  const associatedByFriends =
    enrollment.blockedByFriends?.filter((friend) => friend.originalEnrollment) ?? []
  const isGoOutAloneAdminAccepted = !!canGoOutAlone && enrollment.canGoOutAlone === true
  const isGoOutAloneParentAuthorizationPending =
    enrollment.parentAuthorizedToGoOutAlone === null ||
    enrollment.parentAuthorizedToGoOutAlone === undefined
  const isGoOutAloneAuthorizationPending = !!(
    canGoOutAlone &&
    enrollment.canGoOutAlone &&
    enrollment.authorizedAt &&
    isGoOutAloneParentAuthorizationPending
  )

  const isGoOutAloneAuthorized =
    canGoOutAlone && enrollment.canGoOutAlone && enrollment.parentAuthorizedToGoOutAlone === true

  const disableCanGoOutAloneToggle =
    !canGoOutAlone || enrollment.parentAuthorizedToGoOutAlone != null

  const queryClient = useQueryClient()
  const { mutateAsync } = useMutation({
    mutationFn: (input: UpdateInput<EnrollmentInput>) =>
      updateEnrollmentClientContext(input, getEnrollmentClientContextJourneyFields(user.role)),
    onSuccess: (data) => {
      closeAll()
      observer.notify([
        'enrollment-profile-monitoring-payment-record',
        'enrollment-overpayment',
        'enrollment-profile'
      ])
      // the go-out-alone rule depends on the child's age, refetch it after an edit
      queryClient.invalidateQueries({ queryKey: ['can-enrollment-go-out-alone', enrollment.id] })
      if (
        data.childBirthDate !== enrollment.childBirthDate ||
        data.childGender !== enrollment.childGender ||
        data.childFirstName !== enrollment.childFirstName ||
        data.childLastName !== enrollment.childLastName ||
        data.isAA !== enrollment.isAA ||
        EnrollmentProperties.declaredFriendsChanged(
          enrollment.declaredFriends ?? [],
          data.declaredFriends ?? []
        )
      )
        toggleNotificationModal('email-notification')
      setEnrollment(data)
    }
  })

  const { mutateAsync: updateFriends, isPending: isUpdateFriendsPending } = useMutation({
    mutationFn: (input: UpdateInput<EnrollmentInput>) =>
      updateEnrollmentClientContext(input, getEnrollmentClientContextJourneyFields(user.role))
  })

  const {
    mutateAsync: updateEnrollmentPropositionMutation,
    isPending: isUpdateEnrollmentPropositionPending
  } = useMutation({
    mutationFn: updateEnrollmentProposition,
    onError: showAxiosError
  })

  const { mutateAsync: updateOtoIndividualPropositionMutation } = useMutation({
    mutationFn: updateOtoIndividualProposition,
    onError: showAxiosError
  })

  const { mutateAsync: newPropositionStateMutation, isPending: isNewPropositionStatePending } =
    useMutation({
      mutationFn: newPropositionState,
      onError: showAxiosError
    })

  const {
    mutateAsync: authorizeChildToGoOutAloneMutation,
    isPending: isAuthorizeChildToGoOutAlonePending
  } = useMutation({
    mutationFn: authorizeChildToGoOutAlone,
    onError: () => {
      setEnrollment({ ...enrollment, canGoOutAlone: false })
    },
    onSuccess: () => {
      observer.notify(['enrollment-profile'])
    }
  })

  const {
    mutateAsync: resendAuthorizationToGoOutAloneMutation,
    isPending: isResendAuthorizationToGoOutAlonePending
  } = useMutation({
    mutationFn: resendAuthorizationToGoOutAlone,
    onSuccess: () => {
      toast.success("L'email d'autorisation a été renvoyé avec succès aux parents.")
    }
  })

  const { mutateAsync: unauthorizeChildToGoOutAloneMutation } = useMutation({
    mutationFn: unauthorizeChildToGoOutAlone,
    onError: () => {
      setEnrollment({ ...enrollment, canGoOutAlone: true })
    },
    onSuccess: () => {
      observer.notify(['enrollment-profile'])
    }
  })

  const {
    mutateAsync: acceptCanGoOutAloneAuthorizationMutation,
    isPending: isAcceptCanGoOutAloneAuthorizationPending
  } = useMutation({
    mutationFn: acceptCanGoOutAloneAuthorization,
    onSuccess: () => {
      setEnrollment({ ...enrollment, parentAuthorizedToGoOutAlone: true })
      observer.notify(['enrollment-client-personal-space'])
      toast.success('Autorisation de sortie validée avec succès')
    }
  })

  const {
    mutateAsync: rejectCanGoOutAloneAuthorizationMutation,
    isPending: isRejectCanGoOutAloneAuthorizationPending
  } = useMutation({
    mutationFn: rejectCanGoOutAloneAuthorization,
    onSuccess: () => {
      setEnrollment({ ...enrollment, parentAuthorizedToGoOutAlone: false })
      observer.notify(['enrollment-client-personal-space'])
      toast.success('Autorisation de sortie annulée avec succès')
    }
  })

  const parent1 = EnrollmentProperties.getParentInformation(enrollment, '1')
  const parent2 = EnrollmentProperties.getParentInformation(enrollment, '2')
  const priorityParent = EnrollmentProperties.getParentsByPriority(enrollment)

  const isCenterLocked = enrollment.enrollmentCenter?.center.locked

  // a One to One Individuel enrollment has no journey, no trip and no family arrangement: those
  // boxes have no equivalent for it and its stay is described by its own proposition
  const isOtoIndividual = EnrollmentProperties.isOtoIndividual(enrollment)
  const stayDates = EnrollmentProperties.getEnrollmentDepartureAndArrivalDates(enrollment).value
  // a One to One Individuel stay lays its cells out on two even columns so the right hand values
  // (Pays, Heures de cours) line up; a journey keeps its wide first cell over a three column grid
  const stayRowClassName = cn('grid gap-6 mobile:grid-cols-1 mobile:gap-2', {
    'grid-cols-2': isOtoIndividual,
    'grid-cols-3': !isOtoIndividual
  })
  const stayMainCellClassName = cn({ 'col-span-2 mobile:col-span-1': !isOtoIndividual })
  const insurances = isOtoIndividual
    ? enrollment.otoIndividualProposition?.insurances
    : enrollment.mainProposition?.insurances

  const handlePropositionSubmit = async (values: EnrollmentPropositionInput) => {
    const { declaredFriends, ...input } = values
    // the journey proposition form is never mounted for a One to One Individuel enrollment
    const mainProposition = enrollment.mainProposition
    if (!mainProposition) return

    const { state } = await newPropositionStateMutation({
      id: mainProposition.id,
      enrollmentId: enrollment.id,
      ...mapEnrollmentPropositionInputToRequest(input)
    })

    if (state === 'NO_SPACE_TO_ASSIGN') {
      toggleNotificationModal('no-space-to-assign')
      closeAll()
      return
    }

    // update the enrollment friendl wishlist
    const newEnrollment = await updateFriends({ id: enrollment.id, declaredFriends })

    const data = await updateEnrollmentPropositionMutation({
      id: mainProposition.id,
      enrollmentId: enrollment.id,
      ...mapEnrollmentPropositionInputToRequest(input)
    })

    if (
      EnrollmentProperties.declaredFriendsChanged(
        enrollment.declaredFriends ?? [],
        declaredFriends ?? []
      ) ||
      EnrollmentPropositionProperties.hasPropositionChanged(mainProposition, input)
    )
      toggleNotificationModal('email-notification')

    setEnrollment({ ...newEnrollment, mainProposition: data })
    // a cheaper stay can leave the enrollment overpaid: LEC is asked whether to refund
    observer.notify(['enrollment-profile-monitoring-payment-record', 'enrollment-overpayment'])
    closeModel('select-journey')
  }

  // the popup re-seeds itself from these defaults, so the identity has to stay stable between
  // renders or an open popup would lose the edits in progress
  const otoPropositionInput = useMemo(
    () =>
      enrollment.otoIndividualProposition &&
      hasOtoIndividualCatalogue(enrollment.otoIndividualProposition)
        ? mapOtoIndividualPropositionToInput(enrollment.otoIndividualProposition)
        : undefined,
    [enrollment.otoIndividualProposition]
  )

  const handleOtoPropositionSubmit = async (
    values: OtoIndividualPropositionInput,
    modalKey: 'edit-oto' | 'oto-insurance'
  ) => {
    const proposition = enrollment.otoIndividualProposition
    if (!proposition) return

    const request = mapOtoIndividualPropositionInputToRequest(values)
    await updateOtoIndividualPropositionMutation({ id: enrollment.id, ...request })

    // the PATCH answers with the raw proposition row, without the nested relations the boxes
    // read, so refetch the enrollment instead of merging a partial proposition into the context
    observer.notify([
      'enrollment-profile-monitoring-payment-record',
      'enrollment-overpayment',
      'enrollment-profile'
    ])

    const propositionChanged =
      request.productId !== proposition.product.id ||
      request.departureDate !== proposition.departureDate ||
      (request.returnDate !== undefined && request.returnDate !== proposition.returnDate) ||
      request.numberOfWeeks !== proposition.numberOfWeeks ||
      request.numberOfHours !== proposition.numberOfHours ||
      request.regionOfStayId !== proposition.regionOfStay.id ||
      (request.optionId ?? null) !== (proposition.option?.id ?? null) ||
      (request.optionPrice ?? null) !== (proposition.optionPrice ?? null) ||
      request.arrivalPlaceId !== proposition.arrivalPlace.id ||
      request.transferMethod !== proposition.transferMethod ||
      (request.transferPrice ?? null) !== (proposition.transferPrice ?? null) ||
      EnrollmentPropositionProperties.hasPricingChanged(proposition.pricing, request.pricing) ||
      (request.promoCode ?? null) !== (proposition.promoCode?.code ?? null) ||
      (request.insuranceIds !== undefined &&
        InsuranceProperties.haveInsurancesChanged(proposition.insurances, request.insuranceIds))

    if (propositionChanged) toggleNotificationModal('email-notification')
    closeModel(modalKey)
  }

  const handleInsurancesSubmit = async (values: InsuranceLight[]) => {
    // the journey insurance modal is never mounted for a One to One Individuel enrollment
    if (!enrollment.mainProposition) return
    const data = await updateEnrollmentPropositionMutation({
      id: enrollment.mainProposition.id,
      enrollmentId: enrollment.id,
      cseCode: enrollment.cseRegistration?.cse.code,
      insuranceIds: values.map((insurance) => insurance.id)
    })
    if (
      InsuranceProperties.haveInsurancesChanged(
        enrollment.mainProposition.insurances,
        values.map((insurance) => insurance.id)
      )
    )
      toggleNotificationModal('email-notification')
    setEnrollment({ ...enrollment, mainProposition: data })
    observer.notify(['enrollment-profile-monitoring-payment-record', 'enrollment-overpayment'])
    closeModel('select-insurance')
  }

  const isGoOutAloneParentRequestOpen =
    mode === 'parent' &&
    isEnrollmentConfirmed &&
    isGoOutAloneAdminAccepted &&
    isGoOutAloneParentAuthorizationPending

  // the parent comes from the authorization email: the request must not be missed below the fold
  const [isGoOutAloneParentModalDismissed, setIsGoOutAloneParentModalDismissed] = useState(false)

  const goOutAloneParentRequest = (
    <InnerBox className="bg-white-active py-3 px-4 rounded-lg ">
      <div className="flex items-center gap-2 mobile:flex-col">
        <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
          Demande de Validation pour l'Autorisation de Sortie en Soirée
        </Typography>
        <div className="flex items-center gap-2 mobile:mt-3">
          <Button
            variant="success"
            icon={CheckMarkIcon}
            label="Valider"
            loading={isAcceptCanGoOutAloneAuthorizationPending}
            onClick={async () => {
              await acceptCanGoOutAloneAuthorizationMutation(enrollment.id)
            }}
          />
          <Button
            variant="destructive"
            icon={XIcon}
            label="Refuser"
            loading={isRejectCanGoOutAloneAuthorizationPending}
            onClick={async () => {
              await rejectCanGoOutAloneAuthorizationMutation(enrollment.id)
            }}
          />
        </div>
      </div>
    </InnerBox>
  )

  // the boxes of the booked stay, composed differently for a journey and a One to One Individuel
  const stayBox = (
    <EditableInnerBox
      containerMode={mode}
      title="Séjour"
      subtitle={
        <Badge
          variant="primary"
          size="small"
          text={
            isOtoIndividual
              ? (enrollment.otoIndividualProposition?.product.name ?? '-')
              : (enrollment.mainProposition?.journey.reference ?? '-')
          }
        />
      }
      {...(isOtoIndividual
        ? // a One to One Individuel enrollment has no center, so the center lock is
          // meaningless for it
          { onEdit: () => toggleModal('edit-oto', 0) }
        : !isCenterLocked && {
            onEdit: () => toggleModal('select-journey', 0)
          })}
    >
      <div className="space-y-2">
        <div className={stayRowClassName}>
          <TextInformation
            className={stayMainCellClassName}
            title="Dates et durée de séjour"
            component={
              <div className="flex gap-2 mobile:flex-col mobile:items-start">
                <Badge
                  variant="danger"
                  size="small"
                  text={`${EnrollmentProperties.getEnrollmentDates(enrollment).value?.startDate} - ${EnrollmentProperties.getEnrollmentDates(enrollment).value?.endDate}`}
                />
                <Badge
                  variant="danger"
                  size="small"
                  text={
                    isOtoIndividual
                      ? `${enrollment.otoIndividualProposition?.numberOfWeeks ?? '-'} semaine(s)`
                      : valueRender(
                          enrollment.mainProposition?.journey.date.durationInDays,
                          getDurationInDaysString
                        )
                  }
                />
              </div>
            }
          />
          <TextInformation
            title="Pays"
            value={
              getCountryLabel(
                currentLanguage,
                countries?.data.find(
                  (c) => c.key === EnrollmentProperties.getEnrollmentCountry(enrollment).value
                )
              ) || '-'
            }
          />
        </div>

        <div className={stayRowClassName}>
          <TextInformation
            className={stayMainCellClassName}
            title="Formule"
            value={EnrollmentProperties.getEnrollmentFormula(enrollment).value}
          />
          {/* a One to One Individuel stay has no theme: its option is shown in the
                    options box, and the weekly course volume takes that slot instead */}
          {isOtoIndividual ? (
            <TextInformation
              title="Heures de cours / semaine"
              value={valueRender(
                EnrollmentProperties.getEnrollmentHoursPerWeek(enrollment).value,
                (hours) => t('product.number.of.hours.per.week', { hours })
              )}
            />
          ) : (
            <TextInformation
              title="Thème"
              value={EnrollmentProperties.getEnrollmentMainOption(enrollment).value}
            />
          )}
        </div>

        {/* the journey product's weekly lesson volume: a One to One Individuel stay has no journey
            and shows its own course hours in the row above instead */}
        {!isOtoIndividual && (
          <div className={stayRowClassName}>
            <TextInformation
              className={stayMainCellClassName}
              title="Heures de cours / semaine"
              value={valueRender(
                EnrollmentProperties.getEnrollmentHoursPerWeek(enrollment).value,
                (hours) => t('product.number.of.hours.per.week', { hours })
              )}
            />
          </div>
        )}

        {isOtoIndividual && (
          <div className={stayRowClassName}>
            <TextInformation
              className={stayMainCellClassName}
              title="Région de séjour"
              value={valueRender(enrollment.otoIndividualProposition?.regionOfStay.place.name)}
            />
            {/* the parent sees it in its own "Lieu d'arrivée choisi" box */}
            {mode !== 'parent' && (
              <TextInformation
                title="Lieu d'arrivée"
                value={valueRender(enrollment.otoIndividualProposition?.arrivalPlace.title)}
              />
            )}
          </div>
        )}
      </div>
    </EditableInnerBox>
  )
  const optionsBox = (
    <EditableInnerBox
      title="Options"
      containerMode={mode}
      {...(isOtoIndividual
        ? { onEdit: () => toggleModal('edit-oto', 1) }
        : !isCenterLocked && {
            onEdit: () => toggleModal('select-journey', 1)
          })}
    >
      <div className="grid grid-cols-3 gap-6 mobile:grid-cols-1 mobile:gap-2">
        <TextInformation
          className="col-span-2"
          title="Option(s) facultative(s)"
          value={
            isOtoIndividual
              ? // a One to One Individuel proposition carries a single optional option of
                // its own instead of the journey proposition options
                valueRender(
                  getTranslatedName(enrollment.otoIndividualProposition?.option) || undefined
                )
              : valueRender(EnrollmentProperties.getEnrollmentOptions(enrollment).value)
          }
        />
        {/* the family arrangement only exists on a journey proposition */}
        {!isOtoIndividual && (
          <TextInformation
            title="Modalités d'accueil"
            value={valueRender(
              enrollment.mainProposition?.familyArrangement,
              (value) => familyArrangementEnumMapping[value].description
            )}
          />
        )}
      </div>
    </EditableInnerBox>
  )
  // only a journey enrollment has a trip: the box must not even be built for a One to One
  // Individuel one, whose main proposition is null
  const tripBox = isOtoIndividual ? null : (
    <EditableInnerBox
      title="Voyage"
      containerMode={mode}
      {...(!isCenterLocked && {
        onEdit: () => toggleModal('select-journey', 2)
      })}
    >
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-6 mobile:grid-cols-1 mobile:gap-2">
          <TextInformation
            title="Voyage principal"
            value={TripProperties.getTripLabel(enrollment.mainProposition?.mainTrip)}
          />
          <TextInformation
            title="Voyage secondaire"
            value={valueRender(enrollment.mainProposition?.secondaryTrip, (secondaryTrip) =>
              TripProperties.getTripLabel(secondaryTrip)
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-6 mobile:grid-cols-1 mobile:gap-2">
          <TextInformation
            title="Préférence du lieu de séjour"
            value={valueRender(enrollment.mainProposition?.preferredPlace?.name)}
          />
        </div>
      </div>
    </EditableInnerBox>
  )
  const arrivalPlaceBox = (
    <EditableInnerBox
      title="Lieu d'arrivée"
      containerMode={mode}
      // the arrival place is picked on the first step of the One to One Individuel
      // proposition form, together with the stay itself
      onEdit={() => toggleModal('edit-oto', 0)}
    >
      <div className="space-y-3">
        <TextInformation
          title="Lieu d'arrivée choisi"
          value={valueRender(enrollment.otoIndividualProposition?.arrivalPlace.title)}
        />
        {/* the disclaimer addresses the parent, so it is only shown in the client space */}
        {mode === 'parent' && (
          <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
            Vous avez choisi un séjour en formule 1 professeur pour 1 élève avec voyage individuel.
            Nous vous rappelons, comme indiqué dans notre documentation générale, que dans le cadre
            de ce voyage individuel, l’organisation, la réservation et le paiement du transport
            sont à votre charge. Ce voyage individuel s’effectuera donc sous votre seule
            responsabilité.
          </Typography>
        )}
        <TextInformation
          title="Option Accueil-transfert"
          value={valueRender(
            enrollment.otoIndividualProposition?.transferMethod,
            (transferMethod) =>
              transferMethod === OtoIndividualTransferMethodEnum.AIRPORT_PICKUP
                ? 'Souscrite'
                : 'Non souscrite'
          )}
        />
      </div>
    </EditableInnerBox>
  )
  const insurancesBox = (
    <EditableInnerBox
      containerMode={mode}
      title="Assurances en option"
      onEdit={() => toggleModal(isOtoIndividual ? 'oto-insurance' : 'select-insurance')}
    >
      <div className="flex gap-2 flex-wrap">
        {!insurances?.length && <Typography variant="secondary-body">Aucune assurance</Typography>}
        {insurances?.map((insurance) => (
          <Badge
            key={insurance.id}
            variant="grey"
            size="small"
            text={InsuranceProperties.getEnrollmentInsuranceName(insurance)}
          />
        ))}
      </div>
    </EditableInnerBox>
  )

  return (
    <div>
      <div className="flex justify-between gap-3 mobile:flex-col">
        <div className="flex flex-col gap-3 flex-1">
          <EditableInnerBox
            containerMode={mode}
            title="Informations sur le jeune"
            onEdit={() => {
              toggleModal('general-information')
            }}
          >
            <div className="space-y-2">
              <div className="grid grid-cols-5 gap-6 mobile:grid-cols-1 mobile:gap-2">
                <TextInformation
                  title="Sexe"
                  value={valueRender(
                    enrollment.childGender,
                    (gender) => genderOneLetterMapping[gender].label
                  )}
                />
                <TextInformation
                  className="col-span-2 mobile:col-span-1"
                  title="Date de naissance - Âge"
                  component={
                    <div className="flex gap-1">
                      <Badge
                        variant="grey"
                        size="small"
                        text={valueRender(enrollment.childBirthDate, (childBirthDate) =>
                          DateFormatter.DDMMYYYY(childBirthDate)
                        )}
                      />
                      <Badge
                        variant="blue"
                        size="small"
                        text={valueRender(enrollment.childBirthDate, (childBirthDate) =>
                          stayDates
                            ? DateFormatter.calculateRelativeAge(
                                childBirthDate,
                                stayDates.startDate
                              ).toString()
                            : '-'
                        )}
                      />
                    </div>
                  }
                />
                <TextInformation
                  title="Classe"
                  value={valueRender(
                    enrollment.childSchoolLevel,
                    (schoolLevel) => schoolLevelEnumMapping[schoolLevel]
                  )}
                />
                <TextInformation
                  title="Années d'étude"
                  value={valueRender(enrollment.childNumberOfYearsInLanguage)}
                />
              </div>

              <div className="grid grid-cols-3 gap-6 mobile:grid-cols-1 mobile:gap-2">
                <TextInformation
                  title="# de portable du jeune"
                  value={valueRender(enrollment.childPhoneNumber, (childPhoneNumber) =>
                    formatPhoneNumber(childPhoneNumber)
                  )}
                />
                <TextInformation title="Email du jeune" value={enrollment.childEmail ?? '-'} />
              </div>

              <div className="grid grid-cols-4 gap-6 mobile:grid-cols-1 mobile:gap-2">
                <TextInformation
                  title="Nationalité"
                  value={nationalities?.find((c) => c.code === enrollment.childNationality)?.label}
                />
                <TextInformation
                  title="CSE"
                  value={enrollment.cseRegistration?.cse.code ?? 'n/a'}
                />
                <TextInformation
                  title="Niveau de langue"
                  value={valueRender(
                    enrollment.chosenCountryLanguageLevel,
                    (level) => languageLevelEnumMapping[level].label
                  )}
                />
                <TextInformation
                  title="Résultat au test de niveau"
                  value={valueRender(
                    enrollment.languageTestSubmission?.level,
                    (level) => enrollmentScoreEnumMapping[level].code
                  )}
                />
              </div>
            </div>
          </EditableInnerBox>

          {mode === 'admin' && (
            <EditableInnerBox
              containerMode={mode}
              title="Autorisation de sortir seul le soir"
              subtitle={
                <Switch
                  checked={isGoOutAloneAdminAccepted}
                  disabled={disableCanGoOutAloneToggle}
                  onCheckedChange={(value) => {
                    if (disableCanGoOutAloneToggle) return

                    if (value) toggleModal('authorization-child')
                    else {
                      setEnrollment({ ...enrollment, canGoOutAlone: false })
                      unauthorizeChildToGoOutAloneMutation(enrollment.id)
                    }
                  }}
                />
              }
            >
              {enrollment.parentAuthorizedToGoOutAlone === false && (
                <Typography variant="label-semibold" color={tailwindColors.red.DEFAULT}>
                  Les parents ont rejeté l'autorisation
                </Typography>
              )}

              {isGoOutAloneAuthorized && (
                <Typography variant="label-semibold" color={tailwindColors.green.DEFAULT}>
                  Les parents ont accepté l'autorisation
                </Typography>
              )}

              {isGoOutAloneAdminAccepted && isGoOutAloneParentAuthorizationPending && (
                <>
                  <Typography
                    variant="label-semibold"
                    color={tailwindColors.orange.DEFAULT}
                    className="pb-1"
                  >
                    Autorisation en attente de confirmation
                  </Typography>
                  <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                    <strong>Autorisation de sortie en cours de modification</strong> : en attente
                    de confirmation sur l’Espace Client de la part des parents
                  </Typography>
                </>
              )}

              {isGoOutAloneAuthorizationPending && (
                <TextInformation
                  title={`Email d'autorisation / d'interdiction envoyé le ${valueRender(enrollment.authorizedAt, (date) => DateFormatter.DDMMYYYY(date))}`}
                  component={
                    <Button
                      variant="ghost"
                      onClick={() => {
                        resendAuthorizationToGoOutAloneMutation(enrollment.id)
                      }}
                      loading={isResendAuthorizationToGoOutAlonePending}
                      icon={SendIcon}
                      label={`Renvoyer l'e-mail`}
                    />
                  }
                />
              )}

              {!canGoOutAlone && (
                <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                  Vous ne pouvez pas autoriser ce jeune à sortir seul sans l’accompagnement d’un
                  adulte pour ce séjour/âge.
                </Typography>
              )}
            </EditableInnerBox>
          )}

          {mode === 'parent' && (
            <EditableInnerBox containerMode={mode} title="Autorisation de sortie">
              {isEnrollmentConfirmed && isGoOutAloneAdminAccepted ? (
                enrollment.parentAuthorizedToGoOutAlone === null ||
                enrollment.parentAuthorizedToGoOutAlone === undefined ? (
                  goOutAloneParentRequest
                ) : enrollment.parentAuthorizedToGoOutAlone ? (
                  <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                    Vous avez autorisé votre enfant à sortir seul le soir sans accompagnement d'un
                    adulte
                  </Typography>
                ) : (
                  <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                    Vous avez refusé l'autorisation: votre enfant ne pourra pas sortir seul le soir
                    sans accompagnement d'un adulte
                  </Typography>
                )
              ) : (
                <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                  Votre enfant ne peut pas être autorisé à sortir seul le soir sans
                  l'accompagnement d'un adulte pour ce séjour.
                </Typography>
              )}
            </EditableInnerBox>
          )}

          <EnrollmentMedicalInformation
            mode={mode}
            data={enrollment}
            showEmptyInformation={showEmptyMedicalInformation}
            onEdit={
              mode === 'admin' && user.isMasterLec
                ? () => toggleModal('medical-information')
                : undefined
            }
            onMedicalStatementChange={() => observer.notify(['enrollment-profile'])}
          />

          {mode === 'admin' && (
            <>
              <EditableInnerBox containerMode={mode} title="Amis associés">
                <div className="flex gap-3 flex-wrap">
                  {!enrollment.blockedFriends?.length && !associatedByFriends.length && '-'}
                  {enrollment.blockedFriends?.map((friend) => (
                    <Badge
                      key={friend.id}
                      variant="blue"
                      size="small"
                      text={`${friend.firstName} ${friend.lastName} - ${valueRender(friend.type, (type) => t(friendPlaceEnumMapping[type].label))}`}
                    />
                  ))}
                  {associatedByFriends.map((friend) => (
                    <Badge
                      key={`blocked-by-${friend.id}`}
                      variant="blue"
                      size="small"
                      text={`${friend.originalEnrollment?.childFirstName ?? ''} ${friend.originalEnrollment?.childLastName ?? ''} - ${valueRender(friend.type, (type) => t(friendPlaceEnumMapping[type].label))}`}
                    />
                  ))}
                </div>
              </EditableInnerBox>
              <EditableInnerBox
                title="Amis déclarés"
                onEdit={() => {
                  toggleModal('declared-friends')
                }}
              >
                <div className="flex gap-3 flex-wrap">
                  {enrollment.declaredFriends?.length === 0 && '-'}
                  {enrollment.declaredFriends?.map((friend) => (
                    <Badge
                      key={friend.id}
                      variant="white"
                      size="small"
                      text={`${friend.firstName} ${friend.lastName} - ${valueRender(friend.type, (type) => t(friendPlaceEnumMapping[type].label))}`}
                    />
                  ))}
                </div>
              </EditableInnerBox>
            </>
          )}
        </div>
        <div className="flex flex-col gap-3 flex-1">
          <EditableInnerBox
            containerMode={mode}
            title="Informations sur les parents"
            onEdit={() => {
              toggleModal('parent-information')
            }}
          >
            {enrollment.cseRegistration?.cse.extraOptionEnabled && (
              <>
                <TextInformation
                  title={enrollment.cseRegistration.cse.extraOptionLabel ?? ''}
                  value={valueRender(enrollment.cseRegistration.extraOptionLabel)}
                />
                <div className="h-3" />
              </>
            )}
            <Badge
              variant="warning"
              size="small"
              text={`Parent 1${priorityParent.parent1 === '1' ? ' - Contact prioritaire' : ''}`}
            />
            <div className="h-2" />
            <ParentInformation parent={parent1} mode={mode} />
            <div className="h-3" />
            <Badge
              variant="warning"
              size="small"
              text={`Parent 2${priorityParent.parent1 === '2' ? ' - Contact prioritaire' : ''}`}
            />
            <div className="h-2" />
            <ParentInformation parent={parent2} mode={mode} />
          </EditableInnerBox>
          <EditableInnerBox
            containerMode={mode}
            title="Adresse du contact prioritaire"
            onEdit={() => toggleModal('address-information')}
          >
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-6 mobile:grid-cols-1 mobile:gap-2">
                <TextInformation title="Adresse" value={enrollment.address1} />
                <TextInformation
                  title="Complément d'adresse"
                  value={valueRender(enrollment.address2, (address2) => address2)}
                />
              </div>

              <div className="grid grid-cols-3 gap-6 mobile:grid-cols-1 mobile:gap-2">
                <TextInformation title="Code postal" value={enrollment.postalCode} />
                <TextInformation title="Ville" value={enrollment.city} />
                <TextInformation
                  title="Pays"
                  value={getCountryLabel(
                    currentLanguage,
                    countries?.data.find((c) => c.key === enrollment.country)
                  )}
                />
              </div>
            </div>
          </EditableInnerBox>
          <EditableInnerBox
            containerMode={mode}
            title="Contact en cas d'urgence pendant le séjour"
            onEdit={() => {
              toggleModal('contact-information')
            }}
          >
            <div className="grid grid-cols-2 gap-6 mobile:grid-cols-1 mobile:gap-2">
              <TextInformation
                title="Personne à contacter en cas d'urgence"
                component={
                  <Badge
                    variant="warning"
                    size="small"
                    text={`${valueRender(enrollment.personToContactInCaseOfEmergency, (p) => contactEnumMapping[p])} - ${enrollment.personToContactInCaseOfEmergencyFullName}`}
                  />
                }
              />
              <TextInformation
                title="Numéro de téléphone"
                value={valueRender(enrollment.personToContactInCaseOfEmergencyPhoneNumber, (p) =>
                  formatPhoneNumber(p)
                )}
              />
            </div>
          </EditableInnerBox>
        </div>
      </div>
      <div className="h-3" />
      <EditableInnerBox
        className={cn({ 'bg-white': mode === 'parent' })}
        title={mode === 'parent' ? 'Séjour réservé' : undefined}
      >
        {isOtoIndividual ? (
          // two independent columns: the right hand boxes keep their own height instead of
          // stretching to the row of the taller left hand one
          <div className="grid grid-cols-2 gap-3 mobile:grid-cols-1 mobile:gap-2">
            <div className="flex flex-col gap-3 mobile:gap-2">
              {stayBox}
              {/* the arrival place box addresses the parents only */}
              {mode === 'parent' && arrivalPlaceBox}
            </div>
            <div className="flex flex-col gap-3 mobile:gap-2">
              {optionsBox}
              {insurancesBox}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 mobile:grid-cols-1 mobile:gap-2">
            {stayBox}
            {optionsBox}
            {tripBox}
            {insurancesBox}
          </div>
        )}
      </EditableInnerBox>

      {/* ----------------- Modals ----------------- */}
      <Modal
        isOpen={modalState['medical-information']}
        title={'Informations sur la santé'}
        icon={EditFormIcon}
        hasBorderBottom
        hideActions
        size="medium"
        onCancel={() => closeModel('medical-information')}
      >
        <MedicalInformationForm
          className={MODAL_CONTENT_CLASSNAME}
          canEditMainMedicalInformations={
            mainMedicalInformationsLock?.canEditMainMedicalInformations === true
          }
          defaultValues={{
            medicalInformations: enrollment.medicalInformations,
            medicalInformationsEn: enrollment.medicalInformationsEn,
            additionalMedicalInformations: enrollment.additionalMedicalInformations,
            additionalMedicalInformationsEn: enrollment.additionalMedicalInformationsEn
          }}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('medical-information'),
            onConfirm: (values) => {
              return mutateAsync({ id: enrollment.id, ...values })
            }
          })}
        />
      </Modal>

      <Modal
        isOpen={modalState['general-information']}
        title={'Informations sur le jeune'}
        icon={UserTwoIcon}
        size="medium"
        hasBorderBottom
        hideActions
        onCancel={() => closeModel('general-information')}
      >
        <ChildInformationForm
          className={MODAL_CONTENT_CLASSNAME}
          variant={mode === 'admin' ? 'full' : 'partial'}
          departureDate={stayDates?.startDate}
          defaultValues={mapEnrollmentToChildInformationInput(enrollment)}
          buttonsConfig={modalButtonConfig({
            onConfirm(values) {
              return mutateAsync({ id: enrollment.id, ...values })
            },
            onCancel: () => closeModel('general-information')
          })}
        />
      </Modal>

      <Modal
        isOpen={modalState['parent-information']}
        title={'Informations sur les parents'}
        icon={FamilyIcon}
        hasBorderBottom
        hideActions
        size="medium"
        onCancel={() => closeModel('parent-information')}
      >
        <ParentInformationForm
          enrollmentId={enrollment.id}
          className={MODAL_CONTENT_CLASSNAME}
          defaultValues={mapEnrollmentToParentInformationInput(enrollment)}
          buttonsConfig={modalButtonConfig({
            onConfirm(values) {
              return mutateAsync({ id: enrollment.id, ...values })
            },
            onCancel: () => closeModel('parent-information')
          })}
        />
      </Modal>

      <Modal
        isOpen={modalState['address-information']}
        title={'Adresse du contact prioritaire'}
        icon={LocationOneIcon}
        hasBorderBottom
        hideActions
        onCancel={() => closeModel('address-information')}
      >
        <AddressInformationForm
          className={MODAL_CONTENT_CLASSNAME}
          defaultValues={mapEnrollmentToAddressInformationInput(enrollment)}
          buttonsConfig={modalButtonConfig({
            onConfirm(values) {
              return mutateAsync({ id: enrollment.id, ...values })
            },
            onCancel: () => closeModel('address-information')
          })}
        />
      </Modal>

      <Modal
        isOpen={modalState['contact-information']}
        title={"Contact en cas d'urgence pendant le séjour"}
        icon={TelephoneIcon}
        hasBorderBottom
        hideActions
        onCancel={() => closeModel('contact-information')}
      >
        <ContactInformationForm
          className={MODAL_CONTENT_CLASSNAME}
          defaultValues={mapEnrollmentToContactInformationInput(enrollment)}
          buttonsConfig={modalButtonConfig({
            onConfirm(values) {
              return mutateAsync({ id: enrollment.id, ...values })
            },
            onCancel: () => closeModel('contact-information')
          })}
        />
      </Modal>

      <Modal
        isOpen={isGoOutAloneParentRequestOpen && !isGoOutAloneParentModalDismissed}
        title="Autorisation de sortie"
        icon={EditFormIcon}
        hasBorderBottom
        hideActions
        size="medium"
        onCancel={() => setIsGoOutAloneParentModalDismissed(true)}
      >
        <div className={MODAL_CONTENT_CLASSNAME}>{goOutAloneParentRequest}</div>
      </Modal>

      <Modal
        isOpen={modalState['authorization-child']}
        title="Modifier l'autorisation de sortir seul le soir ? L'e-mail sera envoyé aux parents pour confirmation."
        icon={EditFormIcon}
        hasBorderBottom
        variant="confirmation"
        size="small"
        isLoading={isAuthorizeChildToGoOutAlonePending}
        onCancel={() => closeModel('authorization-child')}
        actionButtonsOptions={{
          cancelText: 'Fermer',
          confirmText: 'Confirmer',
          onConfirm: async () => {
            await authorizeChildToGoOutAloneMutation(enrollment.id)
            closeModel('authorization-child')
          }
        }}
      />

      <Modal
        isOpen={modalState['declared-friends']}
        title={'Ajouter un ami du participant'}
        hideActions
        size="medium"
        icon={UserSixIcon}
        onCancel={() => closeModel('declared-friends')}
      >
        <FriendsForm
          className={MODAL_CONTENT_CLASSNAME}
          defaultValues={mapEnrollmentToFriendsInput(enrollment)}
          onCancel={() => closeModel('declared-friends')}
          onSubmit={async (declaredFriends) => {
            await mutateAsync({ id: enrollment.id, declaredFriends })
          }}
        />
      </Modal>

      {/* a One to One Individuel enrollment has no journey proposition to edit */}
      {enrollment.mainProposition && (
        <EnrollmentPropositionFormModal
          defaultIndex={selectedData}
          isOpen={modalState['select-journey']}
          mode={mode}
          onClose={() => closeModel('select-journey')}
          handleSubmit={handlePropositionSubmit}
          isLoading={
            isUpdateEnrollmentPropositionPending ||
            isUpdateFriendsPending ||
            isNewPropositionStatePending
          }
          defaultValues={{
            ...mapEnrollmentPropositionToInput(enrollment.mainProposition, {
              cseCode: enrollment.cseRegistration?.cse?.code
            }),
            declaredFriends: enrollment.declaredFriends
          }}
        />
      )}

      {/* the One to One Individuel proposition is edited through its own form and its own
          insurance modal, which re-patches the whole proposition */}
      {otoPropositionInput && (
        <>
          <OtoIndividualPropositionFormModal
            isOpen={modalState['edit-oto']}
            defaultIndex={selectedData ?? 0}
            defaultValues={otoPropositionInput}
            childBirthDate={enrollment.childBirthDate}
            onClose={() => closeModel('edit-oto')}
            handleSubmit={(values) => handleOtoPropositionSubmit(values, 'edit-oto')}
          />

          <EnrollmentPropositionInsuranceModal
            isOpen={modalState['oto-insurance']}
            cseCode={enrollment.cseRegistration?.cse.code}
            residenceCountry={enrollment.country}
            destinationCountry={enrollment.otoIndividualProposition?.product.country.id}
            defaultValues={otoPropositionInput.insurances}
            onClose={() => closeModel('oto-insurance')}
            onSubmit={async (insurances) =>
              await handleOtoPropositionSubmit(
                { ...otoPropositionInput, insurances },
                'oto-insurance'
              )
            }
          />
        </>
      )}

      <EnrollmentPropositionInsuranceModal
        cseCode={enrollment.cseRegistration?.cse.code}
        residenceCountry={enrollment?.country}
        destinationCountry={enrollment.mainProposition?.journey?.date.product.country.id}
        defaultValues={InsuranceProperties.mapEnrollmentInsurancesToInput(
          enrollment.mainProposition?.insurances
        )}
        isOpen={modalState['select-insurance']}
        disableTypes={(() => {
          const departureDate = enrollment.mainProposition?.journey?.date.departureDate
          // Can be added or removed until Day -1.
          const disabledRepartition =
            !!departureDate &&
            !isBefore(startOfDay(new Date()), startOfDay(new Date(departureDate)))
          return [
            InsuranceTypeEnum.CANCELLATION,
            ...(disabledRepartition ? [InsuranceTypeEnum.REPATRIATION] : [])
          ]
        })()}
        onClose={() => closeModel('select-insurance')}
        onSubmit={async (insurances) => await handleInsurancesSubmit(insurances)}
      />

      <EnrollmentModificationNotificationModal
        enrollmentId={enrollment.id}
        isOpen={notificationModalState['email-notification']}
        closeModal={() => closeNotificationModal('email-notification')}
      />

      <Modal
        isOpen={notificationModalState['no-space-to-assign']}
        title="Il n'y a plus de places disponibles dans ce centre pour ce séjour, cette option ou ce voyage. Veuillez contacter le bureau des Affaires étrangères pour effectuer un changement."
        icon={ExclamationMarkIcon}
        variant="confirmation"
        size="medium"
        onCancel={() => closeNotificationModal('no-space-to-assign')}
        actionButtonsOptions={{ cancelText: 'Fermer' }}
      />
    </div>
  )
}

export default EnrollmentProfileGlobalView
